import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CONFIG_CONSTANTS, API_CONSTANTS } from '@finder/shared/constants/app.constants';
import { ConfigService } from '@finder/core/config.service';
import getApiBase from '@finder/shared/utils/string/getApiBase';
import { SearchSelection } from '@finder/features/search/interfaces/search-selection.interface';
import { AI_SEARCH_SERVICE } from '@finder/features/ai-search/ai-search.constants';
import { CorrelationService } from '@wdpr/ra-angular-correlation';

@Injectable({
    providedIn: 'root'
})
export class SearchServiceConnectorService {

    private baseUrl: string;
    private version: string;
    private siteId: string;

    constructor(
        private http: HttpClient,
        private config: ConfigService,
        private correlationService: CorrelationService
    ) {
        this.baseUrl = this.config.getValue(CONFIG_CONSTANTS.apiUrlKey);
        this.version = this.config.getValue(CONFIG_CONSTANTS.defaultVersionKey);
        this.siteId = this.config.getValue(CONFIG_CONSTANTS.siteIdKey);
    }

    /**
     * Get Search results for a given term.
     *
     * @param searchTerm the query to search
     * @param searchCategory the category to search
     * @param pageNumber the current page number used for pagination
     * @param version web api version
     * @param swid the user swid
     * @returns an observable
     */
    public getSearchQuery(
        searchTerm: string,
        searchCategory: string,
        pageNumber: number,
        version?: string,
        swid?: string | boolean
    ): Observable<any> {
        const url = this.apiBaseUrl(API_CONSTANTS.searchQueryEndpoint, this.baseUrl, version);

        const params = new URLSearchParams({
            term: searchTerm,
            ...(searchCategory && { searchCategory }),
            page: pageNumber.toString(),
            ...(swid && { swid: swid.toString() })
        });

        return this.http.get(`${url}/${this.siteId}/?${params.toString()}`);
    }

    /**
     * Post Search Selection for analytics history of the user
     *
     * @param options the search selection options
     * @param searchId the search id key from the Discovery Service
     * @param discoveryServiceEnabled flag indicating if Discovery Service is enabled
     * @param swid the user swid (if available)
     * @returns an observable
     */
    public postSearchSelectionHistory(options: SearchSelection, searchId: string, discoveryServiceEnabled: boolean, swid: string | boolean) {
        const url = `${this.apiBaseUrl(API_CONSTANTS.searchSelectionHistoryEndpoint, this.baseUrl)}/${this.siteId}`;

        // Supplemental fields to support the Discovery Service search selection/history API
        const params: any = {
            clientType: AI_SEARCH_SERVICE.clientType,
            discoveryServiceEnabled,
            searchId,
            // sessionId, // TODO: This value should come from the Discovery Service results when it becomes available.
            siteId: this.siteId,
            swid,
            userId: swid,
            userType: AI_SEARCH_SERVICE.guest,
            ...options // Merge the options object into params
        };

        const conversationUUID = this.correlationService.getCookieData(
            this.correlationService.getConversationCookieName()
        );

        // correlation ID is not available elsewhere, so we generate a new one for use in selections
        const correlationUUID = this.correlationService.getUuid();

        fetch(url, {
            method: 'POST',
            body: JSON.stringify(params),
            keepalive: true,
            headers: {
                'X-Conversation-Id': conversationUUID,
                'X-Correlation-Id': correlationUUID,
                'X-Disney-Internal-Site': this.siteId
            },
            credentials: 'include'
        });
    }

    /**
     * Constructs the options and endpoint URL
     *
     * @param endpoint  endpoint
     * @param baseUrl   base URL
     * @param version   api version -- defaulted to v1
     */
    private apiBaseUrl(endpoint: string, baseUrl: string, version: string = this.version): string {
        const apiBase = getApiBase(baseUrl, version);

        return `${apiBase}/${endpoint}`;
    }

}
